import { AmDurationType, AmRooms, daysBetween, FinnairDeeplinkInterface, TravelType } from '../common';
import { EnvConfig, Url } from '../config';
import { HttpParams } from '../services';
import { AmSelectionState, SelectionState } from '../store';
import { sanitize } from './dataUtils';
import { asCmsLang } from './envUtils';

export const bookingFlowDeepLink = (
  { bookingUrl }: EnvConfig,
  {
    locale,
    locations,
    paxAmount,
    travelClass,
    travelDates,
    travelType,
    directFlights,
    promoCode,
    activeLocationIndex,
  }: SelectionState,
  isAward: boolean
): Url => {
  const { adults, c15s, children, infants } = paxAmount;

  const flights = [
    {
      origin: locations[0].origin.locationCode,
      destination: locations[0].destination.locationCode,
      departureDate: travelDates?.departureDate?.toString(),
    },
  ];

  if (travelType === TravelType.ROUND_TRIP) {
    flights.push({
      origin: locations[activeLocationIndex].destination.locationCode,
      destination: locations[activeLocationIndex].origin.locationCode,
      departureDate: travelDates?.returnDate?.toString(),
    });
  }

  if (travelType === TravelType.MULTI_CITY) {
    flights.push({
      origin: locations[1].origin.locationCode,
      destination: locations[1].destination.locationCode,
      departureDate: travelDates?.returnDate?.toString(),
    });
  }

  const urlParams: FinnairDeeplinkInterface = {
    flights,
    cabin: travelClass.toUpperCase(),
    adults,
    c15s,
    children,
    infants,
    directFlights,
    isAward: isAward || undefined,
    promoCode: isAward ? undefined : promoCode,
  };

  Object.keys(urlParams).forEach((key) => {
    if (urlParams[key] === (undefined || null)) {
      delete urlParams[key];
    }
  });

  const params = encodeURIComponent(JSON.stringify(urlParams));
  return { baseUrl: bookingUrl, path: `/${asCmsLang(locale)}/booking/flight-selection?json=${params}` };
};

export const generateGroupString = (rooms: AmRooms): string => {
  return rooms
    .map(({ adults, childrenAges }): string => {
      const adultsString = `${adults}A`;
      const childAges = childrenAges?.join('-');
      return adultsString + ((childAges && `-${childAges}`) || '');
    })
    .join('_');
};

type AmDeepLinkParams = {
  dep: string; // Departure airport code
  arr: string; // Destination code
  g: string; // rooms as group string
  dd: string; // departure date (no time)
  dur: string; // duration, either a number or duration code
  flex: boolean; // Whether or not to allow flexible departure dates
};

export const amDeepLink = (
  {
    amTravelDates: { departureDate, returnDate, flexible },
    locations: { origin, destination, holidayType },
    duration,
    rooms,
  }: AmSelectionState,
  { amDeeplinkUrl }: EnvConfig
): string => {
  const params: AmDeepLinkParams = {
    dep: origin?.locationCode,
    arr: destination?.destinationCode,
    dd: departureDate,
    dur: holidayType.type === AmDurationType.LIST ? duration : `${daysBetween(departureDate, returnDate)}`,
    g: generateGroupString(rooms),
    flex: flexible && holidayType.type === AmDurationType.LIST,
  };

  const urlParams = new HttpParams(sanitize(params)).toString();
  return amDeeplinkUrl + urlParams;
};
